import { Button, Card, CardBody, CardHeader, Divider } from '@heroui/react';
import React, { useCallback, useState } from 'react';

import { useNotification } from '../../../../../contexts/NotificationContext';
import { useGetUserCognitoState } from '../../../hooks/useGetUserCognitoState';
import { useSyncCognitoUser } from '../../../hooks/useSyncCognitoUser';

import { CognitoStateContent } from './CognitoStateContent';
import { InfoTooltip } from './InfoTooltip';

interface CognitoStateSectionProps {
  userId?: string;
}

export enum CognitoSyncStatus {
  SYNCED = 'Synced',
  NOT_CREATED = 'NotCreated',
  CREATED = 'Created',
  CORRUPTED = 'Corrupted',
}

const getStatusText = (status: string): string => {
  switch (status) {
    case CognitoSyncStatus.SYNCED:
      return '🟢';
    case CognitoSyncStatus.NOT_CREATED:
    case CognitoSyncStatus.CREATED:
      return '🟡';
    case CognitoSyncStatus.CORRUPTED:
      return '🔴';
    default:
      return '⚪️';
  }
};

const hasValidCognitoResponse = (
  cognitoState: any
): cognitoState is {
  __typename: 'UserCognitoState';
  status: string;
  cognitoUsers?: {
    email?: { status: string };
    phone?: { status: string };
  };
} => {
  return cognitoState?.__typename === 'UserCognitoState';
};

export const CognitoStateSection: React.FC<CognitoStateSectionProps> = ({
  userId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { addNotification } = useNotification();
  const {
    cognitoState,
    loading: queryLoading,
    refetch,
  } = useGetUserCognitoState(userId);
  const { syncUser, loading: syncLoading } = useSyncCognitoUser();

  const handleSync = useCallback(async () => {
    if (!userId) {
      addNotification({
        title: 'ERROR',
        message: 'User ID is required to sync Cognito state',
        type: 'error',
      });
      return;
    }

    try {
      const result = await syncUser(userId);

      if (result?.__typename === 'SyncCognitoUserSuccess') {
        addNotification({
          title: 'SUCCESS',
          message: 'Cognito state synchronized successfully!',
          type: 'success',
        });
        // Refetch the Cognito state to get updated status
        refetch();
      } else if (result?.__typename === 'SyncCognitoUserFailure') {
        addNotification({
          title: 'ERROR',
          message: result.failureMessage || 'Failed to sync Cognito state',
          type: 'error',
        });
      } else if (result?.__typename === 'NotFoundError') {
        addNotification({
          title: 'ERROR',
          message: result.message || 'User not found',
          type: 'error',
        });
      } else if (result?.__typename === 'AuthorizationFailure') {
        addNotification({
          title: 'ERROR',
          message: result.message || 'Authorization failed',
          type: 'error',
        });
      } else {
        addNotification({
          title: 'ERROR',
          message: 'Unknown error occurred while syncing Cognito state',
          type: 'error',
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      addNotification({
        title: 'ERROR',
        message: `Failed to sync Cognito state: ${errorMessage}`,
        type: 'error',
      });
    }
  }, [userId, syncUser, addNotification, refetch]);

  if (queryLoading) {
    return (
      <Card className="mt-6">
        <CardBody>
          <div className="animate-pulse">
            <div className="h-4 bg-default-200 rounded w-1/4 mb-2"></div>
            <div className="h-3 bg-default-100 rounded w-3/4"></div>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!cognitoState) {
    return null;
  }

  if (!hasValidCognitoResponse(cognitoState)) {
    return (
      <Card className="mt-6 border-danger-200">
        <CardBody>
          <p className="text-danger text-sm">
            Error loading Cognito state:{' '}
            {cognitoState?.message || 'Unknown error'}
          </p>
        </CardBody>
      </Card>
    );
  }

  const statusEmoji = getStatusText(cognitoState.status);
  const isNotSynced = cognitoState.status !== CognitoSyncStatus.SYNCED;

  return (
    <Card className="mt-6">
      <CardHeader className="pb-3">
        <h3 className="text-lg font-semibold text-foreground">Cognito State</h3>
      </CardHeader>
      <Divider />
      <CardBody className="pt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground">Status:</span>
            <span className="inline-flex items-center gap-1 text-sm font-medium">
              {statusEmoji} {cognitoState.status}
            </span>
          </div>
          {isNotSynced && (
            <div className="flex items-center gap-2">
              <InfoTooltip
                header="Sync Cognito"
                content={CognitoStateContent}
              />
              <Button
                color="primary"
                size="sm"
                onClick={handleSync}
                isLoading={syncLoading}
                isDisabled={syncLoading || !userId}
              >
                Sync Cognito
              </Button>
            </div>
          )}
        </div>

        {/* Cognito Users Details */}
        {isNotSynced &&
          cognitoState.cognitoUsers?.email &&
          cognitoState.cognitoUsers?.phone && (
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">
                  Cognito Users:
                </h4>
                {cognitoState.cognitoUsers.email && (
                  <div className="flex items-center gap-2 text-sm mb-1">
                    <span>Email:</span>
                    <span className="inline-flex items-center gap-1 font-medium">
                      {getStatusText(cognitoState.cognitoUsers.email.status)}{' '}
                      {cognitoState.cognitoUsers.email.status}
                    </span>
                  </div>
                )}
                {cognitoState.cognitoUsers.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <span>Phone:</span>
                    <span className="inline-flex items-center gap-1 font-medium">
                      {getStatusText(cognitoState.cognitoUsers.phone.status)}{' '}
                      {cognitoState.cognitoUsers.phone.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Debug Info Toggle */}
        {isNotSynced && (
          <div>
            <Button
              variant="light"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-0 h-auto min-h-0"
            >
              {isExpanded ? 'Hide' : 'Show'} Debug Info
            </Button>
          </div>
        )}

        {/* Debug Info Content */}
        {isNotSynced && isExpanded && (
          <div className="bg-default-50 border border-default-200 rounded-lg p-4 max-h-80 overflow-auto">
            <pre className="text-xs font-mono text-foreground whitespace-pre-wrap">
              {JSON.stringify(cognitoState, null, 2)}
            </pre>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
